/*
 * Generate a JavaScript constructor function from a schema.
 *
 * Objects created with the resulting constructor:
 *
 * - are validated against the schema for that type when the object is created
 * - are frozen (via Object.freeze) and therefore immutable
 * - will work correctly with R.is function (because it conforms to the fantasy-land protocol for '@@type')
 * - show the right name in the Chrome debugger
 *
 * For instance:
 *
 *    // define a constructor for Structure
 *    const Structure = createSchemaBasedConstructor('Structure')
 *
 *    // sample data
 *    const d = {
 *        venueId          : 'dynamicdfw',
 *        id               : 'dynamicdfw-terminala',
 *        name             : 'Terminal A Departures',
 *        shouldDisplay    : true,
 *        isDefaultForVenue: true,
 *    }
 *
 *    // create a structure using the constructor Structure
 *    const s1 = Structure(d.venueId, d.id, d.name, d.shouldDisplay, d.isDefaultForVenue)
 *
 *    // create a second structure using Structure.from function
 *    const s2 = Structure.from(d)
 *
 *    s1.venueId === 'dynamicdfw'  // true
 *    s2.venueId === 'dynamicdfw'  // true
 *    R.is(Structure, s1)          // true
 *    R.is(Structure, s2)          // true
 *
 *    s1.abc = 1                                                       // throws: can't add new field 'abc'
 *    s1.id  = 'fred'                                                  // throws: id is not modifiable
 *    s1 = Structure(d.venueId, d.id, d.name, 8, d.isDefaultForVenue)  // throws: 8 is not a boolean
 *
 *
 *
 *
 *
 * Sadly the implementation is ridiculously complicated:
 *
 * Inheritance
 * -----------
 *
 * Every JavaScript Function points to a prototype object whose 'constructor' field points back to the Function.
 * Any object created later using the constructor will have a __proto__ field that *also* points at Function.prototype.
 * We need to create the Function's prototype object and set up the pointers back and forth.
 *
 * See create-typed-constructor.puml for a diagram
 *
 * Setting up '@@type'
 * -------------------
 *
 * The invisible field '@@type' is set on the constructor so R.is will work (eg. Structure['@@type'] = 'Structure')
 *
 * Setting up 'from'
 * -----------------
 *
 * Define a ("class") function named 'from' that accepts an object and creates a new typed object from it
 * For instance, Structure.from(o) will create a new Structure object using values in o --
 * as long as new object conforms to the schema.
 *
 * eval!
 * -----
 *
 * We would like the debugger to know the type of an Object (not just Ramda) -- but the field used by the
 * debugger is 'name' (eg. Structure.name, not Structure['@@type']. Unfortunately, in JavaScript,
 * the 'name' field is read-only -- and it's set by a fairly complicated algorithm that we can't use,
 * because we're meta-programming here.
 *
 * The only way around this (at least for ES5 environments) is to wrap the constructor function *IN A STRING*
 * with the name we want to use and then define the function using the JavaScript meta-programming 'new Function' syntax
 * See namedFunction
 *
 * Duplicated field names
 * ----------------------
 *
 * There is a very unfortunate duplication of the field names. Even though we know the field names from the schema,
 * we don't know what order the user would like to pass them into the constructor (JSON Schema provides no ability to
 * order properties; the "properties" key of a schema is an object which has no defined order).
 *
 * To get around this, the schema must have an additional 'constructorParameterOrder' key that repeats all the
 * property names in the desired order.
 *
 * Validation / immutable
 * ----------------------
 *
 * Later, when an *object* is created using the constructor we're generating, it is:
 *
 * - constructed from the parameters passed in
 * - frozen to make it immutable
 * - validated against the schema (using the ajv json schema validator)
 *
 * An exception is thrown from the constructor if the validation fails
 *
 */

import { Ajv, Vms } from '@locuslabs/json-schemas';
import { keys, clone, equals, values, difference } from 'ramda';

// returns the function 'fn' with the given 'name' set so the debugger can see it
const namedFunction = (name, fn) =>
    new Function('fn', `return function ${name}(){ return fn.apply(this,arguments)}`)(fn);

/*
 * GENERATE a constructor function (see description at the top).
 * Impure: 'typeName' must be a key in (the "global" variable) 'schemas'
 *
 * @sig :: String -> constructor function
 */
function createSchemaBasedConstructor(typeName) {
    // validate that the SCHEMA will work
    // JSON Schema doesn't define an order for properties -- but we need a fixed order for our constructor
    // so our schema has an *additional* ("non-normative") field named constructorParameterOrder
    // which defines the parameter order to be passed to the constructor
    // Here we're making sure the set of properties equals the set of parameters
    const checkFieldsPassedInAreAlsoInSchema = () => {
        const fieldsInSchema = keys(schema.properties).sort();
        const sortedFields = clone(parameters).sort();

        if (!equals(fieldsInSchema, sortedFields)) {
            throw new Error(
                `Schema '${typeName}' has a mismatch between the 'properties' and 'constructorParameterOrder' keys.` +
                    `properties: ${JSON.stringify(fieldsInSchema)} ` +
                    `constructorParameterOrder: ${JSON.stringify(parameters)}` +
                    `Every property name must appear in the constructorParameterOrder`,
            );
        }
    };

    const schema = Vms[`${typeName}`];
    const parameters = schema.constructorParameterOrder;
    const isObject = schema.type === 'object';

    if (parameters) checkFieldsPassedInAreAlsoInSchema();

    const prototype = {};

    // create a JSON Schema validator to be used each time the constructor is called
    const validateSchema = Ajv.createValidator(schema, ...values(Vms));

    // This is the constructor we will return
    // Its signature depends on the types we found in the schema, but for type T, it will take all the
    // properties defined in T.json and return an object of type T
    function _objectConstructorFunction(...values) {
        const constructedValue = zipAddingPrototype(prototype, parameters, values);
        const errors = validateSchema(constructedValue);

        if (errors) {
            throw new Error(
                errors.length === 1 ? `${typeName}${errors[0].trim()}` : `${typeName}\n${errors.join('\n')}`,
            );
        }

        return constructedValue;
    }

    function _arrayConstructorFunction(...values) {
        const constructedArray = [...values];
        const errors = validateSchema(constructedArray);

        // Can't create an Array from a prototype, so add the constructor here
        addInvisiblePropertyToObject(constructedArray, 'constructor', constructorFunction);

        if (errors) {
            throw new Error(
                errors.length === 1 ? `${typeName}${errors[0].trim()}` : `${typeName}\n${errors.join('\n')}`,
            );
        }

        return constructedArray;
    }

    // Now set the 'name' of the function so it looks right in the debugger
    const constructorFunction = namedFunction(
        typeName,
        isObject ? _objectConstructorFunction : _arrayConstructorFunction,
    );

    // A function's 'prototype' field points to an object with a 'constructor' field which points back to the function
    addInvisiblePropertyToObject(prototype, 'constructor', constructorFunction);
    constructorFunction.prototype = prototype;

    // set the TYPE field for Ramda's 'is'
    constructorFunction['@@type'] = typeName;

    // add (equivalent to "static") functions onto the function itself

    const fromObject = (sourceObject, disallowExtraneous) => {
        if (disallowExtraneous) {
            const extraKeys = difference(keys(sourceObject), schema.constructorParameterOrder).join(', ');
            if (extraKeys.length) throw new Error(`${typeName}.from() doesn't allow extraneous fields: ${extraKeys}`);
        }

        const fieldValue = fieldName => sourceObject[fieldName];
        const args = parameters.map(fieldValue);
        return _objectConstructorFunction.apply(this, args);
    };

    /*
     * @sig fromArray :: [a] -> TypeName
     */
    const fromArray = sourceArray => {
        return _arrayConstructorFunction.apply(this, sourceArray);
    };

    /*
     * Add <TypeName>.from() which will call the constructor <TypeName>() passing it values pulled from a sourceObject
     * If disallowExtraneous is true, every field of the sourceObject must match the name of one of the fields
     * in the <TypeName>'s schema. Otherwise, unused fields in sourceObject will be ignored
     *
     * @sig from :: ({k:v}, Boolean) -> TypeName
     */
    constructorFunction.from = isObject ? fromObject : fromArray;

    return constructorFunction;
}

/*
 * Same as R.zip, except the resulting object has the given prototype (zip uses Object as the prototype)
 * @sig zipAddingPrototype :: ((* -> *), [String], [*]) -> {k:v}
 *
 * @example
 *
 *      zipAddingPrototype(PointPrototype, ['x', 'y'], [1,2)) -> { x: 1 y: 2 }
 */
const zipAddingPrototype = (proto, keys, values) => {
    const obj = Object.create(proto);

    keys.forEach((k, i) => (obj[k] = values[i]));

    return obj;
};

/*
 * Set obj[prop] = value -- but make prop hard to find or change
 * Impure: changes obj
 */
const addInvisiblePropertyToObject = (obj, prop, value) =>
    Object.defineProperty(obj, prop, {
        value,
        enumerable: false,
        writable: false,
        configurable: false,
    });

export { createSchemaBasedConstructor };
